// A styled replacement for window.confirm.
//
// The native dialog can't be styled, announces the page's host name ("localhost
// :3000 says"), and forces everything into one run-on paragraph — so the detail
// that actually matters gets skimmed past. This puts the question in the
// heading and the consequences in a short list underneath, where they read.
//
// Deliberately plain: no async handling, no loading state. Callers do the work
// after onConfirm, exactly as they did after a window.confirm.

import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

interface Props {
  open: boolean;
  /** The question, e.g. "Remove Harish Chopra?" */
  title: string;
  /** One line of context under the title. */
  message?: string;
  /** Consequences, one per line — what changes and what doesn't. */
  points?: string[];
  confirmLabel?: string;
  cancelLabel?: string;
  /** Red confirm button for destructive actions. */
  danger?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmDialog: React.FC<Props> = ({
  open, title, message, points = [],
  confirmLabel = 'Confirm', cancelLabel = 'Cancel',
  danger = false, onConfirm, onCancel,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);

  // Focus lands on the panel, not on a button. The dialog is opened by pressing
  // Enter, and a focused button would be activated by that same keypress (or its
  // auto-repeat) — the dialog would answer itself and vanish before it was read.
  // Focusing the panel still moves focus out of the field behind it, so Tab
  // reaches the buttons and screen readers announce the dialog.
  useEffect(() => {
    if (open) panelRef.current?.focus();
  }, [open]);

  // Esc cancels. Enter deliberately does NOT confirm.
  //
  // These dialogs are often opened *by* pressing Enter — committing a field
  // with the keyboard is what raises the question in the first place. With
  // Enter bound to confirm, the same keypress (or its auto-repeat) sailed
  // straight through the dialog: it appeared for a frame and answered itself,
  // so the user saw no warning at all and the action just happened.
  //
  // Confirming needs a deliberate click, which is the point of asking.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); onCancel(); }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open, onCancel]);

  if (!open) return null;

  return createPortal(
    <div
      // stopPropagation because this is portaled: in the React tree the dialog
      // is still a child of whatever opened it, so a click here would otherwise
      // also reach that component's own click handlers — closing the modal
      // behind the dialog, not just the dialog.
      onClick={(e) => { e.stopPropagation(); onCancel(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 6000,
        background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(2px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
      }}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        style={{
          background: '#fff', borderRadius: '16px', width: '440px', maxWidth: '100%',
          boxShadow: '0 24px 64px rgba(15,23,42,0.28)', overflow: 'hidden',
          fontFamily: '"Inter", sans-serif', outline: 'none',
        }}
      >
        <div style={{ padding: '1.5rem 1.5rem 1.25rem' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.9rem' }}>
            <div
              aria-hidden
              style={{
                width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: danger ? '#fef2f2' : '#eff6ff',
                color: danger ? '#dc2626' : '#2563eb',
                fontSize: '1.15rem', fontWeight: 700,
              }}
            >
              {danger ? '!' : '?'}
            </div>
            <div style={{ minWidth: 0 }}>
              <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.35 }}>
                {title}
              </h3>
              {message && (
                <p style={{ margin: '0.35rem 0 0', fontSize: '0.86rem', color: '#475569', lineHeight: 1.5 }}>
                  {message}
                </p>
              )}
            </div>
          </div>

          {points.length > 0 && (
            <ul style={{
              margin: '1rem 0 0', padding: '0.75rem 0.9rem', listStyle: 'none',
              background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px',
              display: 'flex', flexDirection: 'column', gap: '0.45rem',
            }}>
              {points.map((p, i) => (
                <li key={i} style={{ display: 'flex', gap: '0.5rem', fontSize: '0.84rem', color: '#334155', lineHeight: 1.45 }}>
                  <span aria-hidden style={{ color: '#94a3b8', flexShrink: 0 }}>•</span>
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div style={{
          display: 'flex', justifyContent: 'flex-end', gap: '0.6rem',
          padding: '0.9rem 1.5rem', background: '#f8fafc', borderTop: '1px solid #e2e8f0',
        }}>
          {/* Neither button takes focus — see the panel ref above. A browser
              activates a focused button on Enter, and this dialog is opened by
              pressing Enter, so focusing either one lets the opening keypress
              answer the question for the user. */}
          <button
            onClick={onCancel}
            style={{
              padding: '0.55rem 1.1rem', borderRadius: '8px', border: '1px solid #cbd5e1',
              background: '#fff', color: '#475569', fontSize: '0.86rem', fontWeight: 600,
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            style={{
              padding: '0.55rem 1.1rem', borderRadius: '8px', border: 'none',
              color: '#fff', fontSize: '0.86rem', fontWeight: 700, cursor: 'pointer',
              fontFamily: 'inherit',
              background: danger
                ? 'linear-gradient(135deg, #f87171 0%, #dc2626 100%)'
                : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              boxShadow: danger
                ? '0 4px 10px -2px rgba(220,38,38,0.35)'
                : '0 4px 10px -2px rgba(37,99,235,0.35)',
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ConfirmDialog;
